import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useInventory } from '../../../contexts/InventoryContext';
import ConfigurationDetailsBase from '../projects/project_details/configuration_details/ConfigurationDetailsBase';

/**
 * Config detail page for route: /inventory/projects/:projectId/configurations/:configurationId
 * Lazy loading: project on mount; overview (config), units, and media when their tab is selected.
 */
export function ConfigDetail() {
  const { projectId, configurationId } = useParams();
  const { getProjectDetail, getConfig } = useInventory();
  const [project, setProject] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial load: project only (for header/back link)
  useEffect(() => {
    if (!projectId || !configurationId) {
      setLoading(false);
      setConfig(null);
      return;
    }
    setLoading(true);
    setConfig(null);
    getProjectDetail(projectId)
      .then((proj) => setProject(proj ?? null))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [projectId, configurationId, getProjectDetail]);

  const loadConfig = useCallback(async () => {
    if (!projectId || !configurationId || !getConfig) return null;
    const cfg = await getConfig(projectId, configurationId);
    setConfig(cfg ?? null);
    return cfg;
  }, [projectId, configurationId, getConfig]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, p: 3 }}>
        <CircularProgress size={32} sx={{ color: 'success.main' }} />
      </Box>
    );
  }
  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Project not found.</Typography>
      </Box>
    );
  }

  return (
    <ConfigurationDetailsBase
      projectId={projectId}
      configurationId={configurationId}
      config={config}
      project={project}
      onConfigUpdated={setConfig}
      loadConfig={loadConfig}
    />
  );
}
